import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

const validIdentifier = (v) => /^\d{10}$/.test(v) || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const requestCode = async (e) => {
    e.preventDefault();
    if (!validIdentifier(identifier.trim())) return toast.error('Enter your 10-digit Member ID or registered email');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password/request`, { identifier: identifier.trim() });
      setInfo(data.message);
      if (data.test_otp) toast.info(`Test account code: ${data.test_otp}`);
      setStep(2);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) return toast.error('Enter the 6-digit code');
    if (newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    if (newPassword !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password/verify`, { identifier: identifier.trim(), otp: otp.trim(), new_password: newPassword });
      toast.success('Password reset successful. Please log in with your new password.');
      navigate('/member/login');
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md glass-gold p-8">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <KeyRound className="w-6 h-6 text-[#D4AF37]" /> Forgot Password
        </h1>
        <p className="text-gray-400 text-sm mb-6" data-testid="forgot-password-info">
          {step === 1 ? 'Enter your 10-digit Member ID or registered email. We will send a 6-digit reset code to your registered email.' : info}
        </p>

        {step === 1 ? (
          <form onSubmit={requestCode} className="space-y-5">
            <div>
              <label className="input-label">Member ID or Email</label>
              <input value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="10-digit Member ID or email" className="input-gold" data-testid="forgot-member-id-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="forgot-send-code-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send Code
            </button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-5">
            <div>
              <label className="input-label">6-digit Code</label>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" maxLength={6}
                placeholder="Code from your email" className="input-gold" data-testid="forgot-otp-input" />
            </div>
            <div>
              <label className="input-label">New Password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="input-gold" data-testid="forgot-new-password-input" />
            </div>
            <div>
              <label className="input-label">Confirm New Password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="input-gold" data-testid="forgot-confirm-password-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="forgot-reset-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Reset Password
            </button>
            <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-gray-400 hover:text-white" data-testid="forgot-resend-btn">
              Didn&apos;t get the code? Check spam, or send again
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm">
          <Link to="/member/login" className="text-[#D4AF37] hover:underline" data-testid="forgot-back-to-login">Back to Login</Link>
        </p>
      </motion.div>
    </div>
  );
}
